<template>
  <el-row type="flex" justify="center">
    <el-col :xs="24" :sm="22" :md="18" :lg="14" :xl="10">
      <FullScreenLoading v-if="loading" />
      <div v-if="!loading">
        <el-row>
          <el-col :span="24">
            <section class="breadcrumbs">
              <Paragraph
                :text="`${office} / ${event} / ${diets}`"
                align="left"
                :size="increaseSizeFactor"
              ></Paragraph>
            </section>
            <Heading
              :text="$t('catering.cateringPageTitle')"
              align="center"
              :size="increaseSizeFactor"
            ></Heading>
          </el-col>
        </el-row>
        <el-row type="flex" justify="center">
          <el-col>
            <div class="search-section">
              <Heading
                :text="$t('catering.userSearchTitle')"
                :level="2"
                align="center"
                :size="increaseSizeFactor"
              ></Heading>
              <Paragraph
                :text="$t('catering.contact', { cateringContact })"
                :size="increaseSizeFactor"
                :highlighted="true"
                align="center"
              ></Paragraph>
              <user-autocomplete
                :minlength="3"
                :users="users"
                :settings="settings"
                @confirmation="confirmPickup"
              />
            </div>
            <div class="qr-section">
              <!-- TODO: [JIRA FM-616]: Show this section when QR codes are supported -->
              <template v-if="qrCodeEnabled">
                <p class="or">- {{ $t('catering.or') }} -</p>
                <p class="qr-label">{{ $t('catering.qr') }}</p>
                <div class="qr-container">
                  <svg-icon :icon="qrIcon" class="qr-icon" />
                </div>
              </template>
            </div>
          </el-col>
        </el-row>
      </div>
    </el-col>
  </el-row>
</template>

<script>
import UserAutocomplete from '@/components/catering/UserAutocomplete';
import FullScreenLoading from '@/components/FullScreenLoading';
import SvgIcon from '@/components/SvgIcon';
import Heading from '@/components/Heading';
import Paragraph from '@/components/Paragraph';
import { ROUTES, CATERING_CONTACT, DIET_TYPES } from '@/constants';
import { mapActions, mapState } from 'vuex';
import moment from 'moment';

/**
 * Settings
 * @typedef {Object} CateringSettings
 * @property {string} error - If error exists in the stored settings.
 * @property {Object} settings - Object containg all possible catering settings.
 */

/**
 * Catering Data
 * @typedef {Object} CateringData
 * @property {Object[]} users - Array of users with his/her orders for the day.
 * @property {Object[]} mealsInfo - Array of the meals served that day.
 * @property {string} date - Date of the data.
 */
/**
 * Catering State
 * @typedef {Object} CateringState
 * @property {string} pinCode - PinCode retrieved from a remote endpoint.
 * @property {CateringSettings} settings - Settings
 * @property {CateringData} data - Catering Data fetched from remote server or local storage.
 */

export default {
  name: 'CateringPage',
  components: {
    UserAutocomplete,
    FullScreenLoading,
    SvgIcon,
    Heading,
    Paragraph,
  },
  async created() {
    await this.getSettings();
  },
  data() {
    return {
      cateringContact: CATERING_CONTACT.join(` ${this.$t('catering.or')} `),
      loading: true,
      qrIcon: 'qr-code',
      qrCodeEnabled: false,
      increaseSizeFactor: 2,
    };
  },
  methods: {
    ...mapActions('catering', {
      getSettings: 'getCateringSettings',
      getData: 'getRemoteCateringData',
      checkLocalUserOrder: 'checkLocalUserOrder',
    }),
    ...mapActions('orders', {
      checkUserOrder: 'checkOutUserOrder',
    }),
    async confirmPickup(orderObject) {
      const { userId, orderId } = orderObject;
      const orderProps = { orderId, served: true };
      await this.checkLocalUserOrder({ userId, ...orderProps }); // trigger action to save it locally
      await this.checkUserOrder(orderProps); // trigger action to save it in the remote DB
    },
  },
  computed: {
    ...mapState('catering', {
      settings: ({ catering }) => catering.settings,
      users: ({ catering }) => catering.data.users,
      office: ({ catering }) => catering.settings.settings.office.name,
      event: ({ catering }) => catering.settings.settings.event.name,
      diets({ catering }) {
        const settingsDiets = catering.settings.settings.diets;
        const isAllTypes = settingsDiets.length === DIET_TYPES.length;
        return isAllTypes
          ? this.$t('catering.allDietTypes')
          : settingsDiets.map((diet) => diet.name).join(', ');
      },
    }),
  },
  watch: {
    async settings() {
      const { error, settings } = this.settings;
      const toCateringRoute = error || !settings;
      if (toCateringRoute) {
        this.$router.push(ROUTES.CATERING_SETTINGS);
        return;
      } else {
        const { date, office } = settings;
        const formattedDate = moment(date).format('YYYY-MM-DD');
        this.loading = true;
        await this.getData({ date: formattedDate, office: office.id });
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../scss/variables';
.search-section {
  min-height: 220px;
}
/deep/ .el-autocomplete {
  width: 100%;
}
.or {
  padding-bottom: 20px;
}
.qr-label {
  padding: 40px 0;
}
.qr-container {
  padding: 20px 0 40px;
  text-align: center;
}
.qr-icon {
  cursor: pointer;
  width: 170px;
}
.el-alert--error {
  margin-bottom: 0;
}
.breadcrumbs {
  margin-bottom: 16px;
}
</style>



// WEBPACK FOOTER //
// src/pages/Catering/CateringPage.vue